import { useEffect, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, Lightbulb, Zap, AlertTriangle } from "lucide-react";
import { api, microsoftLoginUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FullScreenLoader } from "@/components/layout/Guards";

const ERRORS = {
  not_configured: "L'accesso Microsoft non è ancora configurato. Contatta l'amministratore di sistema.",
  unauthorized: "Il tuo account Microsoft non è abilitato: chiedi all'amministratore della tua azienda di crearti l'utente.",
  disabled: "Il tuo account è stato disabilitato. Contatta l'amministratore.",
  failed: "Accesso non riuscito. Riprova.",
  expired: "La richiesta di accesso è scaduta. Riprova.",
  session: "La sessione è scaduta. Accedi di nuovo.",
  consent: "La tua azienda deve prima approvare ON! Suite in Microsoft: chiedi al vostro amministratore Microsoft.",
  ms_mismatch: "Account Microsoft diverso da quello del tuo utente.",
};

const INPUT = "h-11 w-full rounded-control border border-zinc-300 px-3 text-sm outline-none transition-colors focus:border-zinc-900";

export const NeutralLogo = ({ inverted = false, className = "" }) => (
  <img src={inverted ? "/brand/on-suite-white.png" : "/brand/on-suite.png"} alt="ON! Suite" className={`h-9 w-auto self-start object-contain ${className}`} data-testid="neutral-logo" />
);

const MsLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const PRINCIPLES = [
  { icon: Eye, title: "Vedere", text: "La situazione aziendale in tempo reale, in un unico punto." },
  { icon: Lightbulb, title: "Capire", text: "Cosa richiede attenzione e quali sono le priorità." },
  { icon: Zap, title: "Agire", text: "Le prossime azioni, pianificate e assegnate." },
];

const BrandPanel = () => (
  <div className="relative hidden overflow-hidden bg-[#0f1012] bg-cover bg-center p-12 lg:flex lg:w-[46%] lg:flex-col lg:justify-between"
    style={{ backgroundImage: "url(/brand/login-neutral.jpg)" }} data-testid="login-brand-panel">
    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
    <NeutralLogo inverted className="relative" />
    <div className="relative max-w-md">
      <h2 className="font-display text-4xl font-bold leading-tight text-white">Dati, persone e attività. Un unico punto di controllo.</h2>
      <div className="mt-10 space-y-5">
        {PRINCIPLES.map(({ icon: Icon, title, text }, i) => (
          <div key={title} className={`flex gap-4 animate-rise delay-${i + 1}`}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-zinc-200"><Icon className="h-4 w-4" strokeWidth={1.75} /></div>
            <div><p className="font-semibold text-white">{title}</p><p className="text-sm text-zinc-300">{text}</p></div>
          </div>
        ))}
      </div>
    </div>
    <p className="relative text-xs text-zinc-400">Accesso riservato agli utenti autorizzati dalla propria azienda.</p>
  </div>
);

const EmailStep = ({ onFound }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { const r = await api.post("/auth/lookup", { email: email.trim() }); onFound({ ...r.data, email: email.trim() }); }
    catch (x) { const d = x.response?.data?.detail; setErr(typeof d === "string" ? d : "Riprova tra poco."); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-8 space-y-3" data-testid="login-email-form">
      <label className="text-xs font-semibold text-zinc-600" htmlFor="login-email">Email di lavoro</label>
      <input id="login-email" type="email" required autoFocus placeholder="nome@azienda.it" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} data-testid="login-email-input" />
      {err && <p className="text-xs text-red-600" data-testid="login-email-error">{err}</p>}
      <button type="submit" disabled={busy} className="group flex h-11 w-full items-center justify-center gap-2 rounded-control bg-zinc-900 text-sm font-semibold text-white transition-colors hover:bg-zinc-700 disabled:opacity-50" data-testid="login-email-continue">
        {busy ? "Verifica…" : "Continua"}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
    </form>
  );
};

const CompanyStep = ({ acc, onBack, msReady }) => {
  const [email, setEmail] = useState(acc.email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const accent = acc.color ? { backgroundColor: acc.color } : undefined;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api.post("/auth/password-login", { email: email.trim(), password }); window.location.href = "/dashboard"; }
    catch (x) { const d = x.response?.data?.detail; setErr(typeof d === "string" ? d : "Accesso non riuscito."); setBusy(false); }
  };
  return (
    <div className="mt-8 animate-rise" data-testid="login-company-step">
      <div className="flex items-center gap-3 rounded-control border border-zinc-200 p-3">
        {acc.logo ? <img src={acc.logo} alt={acc.name} className="h-9 w-auto max-w-[140px] object-contain" data-testid="login-company-logo" />
          : <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 text-sm font-bold text-zinc-700">{(acc.name || acc.email)[0].toUpperCase()}</span>}
        <div className="min-w-0 flex-1">
          {acc.name && <p className="truncate text-sm font-semibold text-zinc-900" data-testid="login-company-name">{acc.name}</p>}
          {acc.email && <p className="truncate text-xs text-zinc-500">{acc.email}</p>}
        </div>
        {onBack && <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-zinc-900" data-testid="login-change-email"><ArrowLeft className="h-3.5 w-3.5" />Cambia</button>}
      </div>
      {acc.methods.includes("microsoft") && <>
        <a href={msReady ? microsoftLoginUrl : undefined} aria-disabled={!msReady} data-testid="login-microsoft-entra-button"
          className={`mt-5 flex h-11 w-full items-center justify-center gap-3 rounded-control bg-brand-600 text-sm font-semibold text-white transition-colors hover:bg-brand-700 ${msReady ? "" : "pointer-events-none opacity-50"}`}>
          <span className="rounded-[3px] bg-white p-[3px]"><MsLogo /></span> Accedi con Microsoft
        </a>
        {!msReady && <p className="mt-2 text-xs text-amber-700" data-testid="login-microsoft-not-configured">Accesso Microsoft non ancora configurato su questo ambiente.</p>}
      </>}
      {acc.methods.includes("password") && (
        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="login-password-form">
          {!acc.email && <input type="email" required autoFocus placeholder="Email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} data-testid="login-password-email" />}
          <input type="password" required autoFocus={!!acc.email} placeholder="Password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT} data-testid="login-password-password" />
          {err && <p className="text-xs text-red-600" data-testid="login-password-error">{err}</p>}
          <button type="submit" disabled={busy} style={accent} className="h-11 w-full rounded-control bg-zinc-900 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="login-password-submit">{busy ? "Accesso…" : "Accedi"}</button>
        </form>
      )}
    </div>
  );
};

export default function Login() {
  const { status } = useAuth();
  const [params] = useSearchParams();
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [acc, setAcc] = useState(null);
  const [badSlug, setBadSlug] = useState(false);
  const error = ERRORS[params.get("error")] || (badSlug ? "Indirizzo di accesso non valido: inserisci la tua email." : null);

  useEffect(() => {
    api.get("/auth/config").then((r) => setConfig(r.data)).catch(() => setConfig({ microsoft_configured: false }));
  }, []);
  useEffect(() => {
    if (!slug) { return; }
    api.get(`/auth/company/${encodeURIComponent(slug)}`).then((r) => setAcc({ ...r.data, email: "", fixed: true })).catch(() => setBadSlug(true));
  }, [slug]);

  if (status === "loading") return <FullScreenLoader />;
  if (status === "authed") return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex min-h-screen bg-white" data-testid="login-page">
      <BrandPanel />
      <div className="flex flex-1 items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-rise">
          {!acc && <NeutralLogo className="mb-10 lg:hidden" />}
          <h1 className="font-display text-[28px] font-bold tracking-tight text-zinc-900">{acc?.name ? `Accedi a ${acc.name}` : "Accedi a ON! Suite"}</h1>
          <p className="mt-2 text-sm text-zinc-500">{acc ? "Completa l'accesso con il metodo della tua azienda." : "Inserisci la tua email: ti portiamo all'accesso della tua azienda."}</p>
          {error && (
            <div className="mt-6 flex gap-2 rounded-control border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert" data-testid="login-error-message">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}
            </div>
          )}
          {acc ? <CompanyStep acc={acc} onBack={acc.fixed ? null : () => setAcc(null)} msReady={!!config?.microsoft_configured} /> : <EmailStep onFound={setAcc} />}
          <p className="mt-8 text-xs leading-relaxed text-zinc-400">Accesso consentito solo agli utenti autorizzati dall'amministratore della propria azienda.</p>
        </div>
      </div>
    </div>
  );
}
